import {useMutation, useQuery} from '@tanstack/react-query';
import { useActor } from 'contexts/ActorContext';
import {
    fetchOwnerContext,
    fetchClassContext,
    fetchActorLibrary,
    fetchTitleSuggest,
    fetchTitleWelcome,
    fetchVideoContext,
    writeActorEmotion
} from 'api';

import type {OwnerContextResponseMessage} from '@/types/OwnerContextResponse';
import type {ClassContextResponseMessage} from '@/types/ClassContextResponse';
import type {ActorLibraryResponseMessage} from '@/types/ActorLibraryResponse';
import type {TitleSuggestResponseMessage} from '@/types/TitleSuggestResponse';
import type {TitleWelcomeResponseMessage} from '@/types/TitleWelcomeResponse';
import type {VideoContextResponseMessage} from '@/types/VideoContextResponse';

/**
 * Hook: Video Context
 * Returns context data for a given video, keyed also by version to force refetches.
 */
export function useVideoContextQuery(musicVideo: string, version?: number) {
  const { actor } = useActor();
  const secure = Boolean(actor && actor.limitUsage === false);
  return useQuery<VideoContextResponseMessage | null, Error>(
    ['videoContext', secure, musicVideo, version],
    async () => {
      try {
        return await fetchVideoContext(musicVideo, secure);
      } catch (err: any) {
        // Treat 404 as not-found (null data)
        if (err.response?.status === 404) {
          return null;
        }
        throw err;
      }
    },
    {
      enabled: !!musicVideo,
      keepPreviousData: true,
      suspense: false,
      useErrorBoundary: false,
      retry: false,
    }
  );
}

/**
 * Hook: Owner Context
 * Returns owner page data, optionally filtered by class and language.
 */
export function useOwnerContextQuery(
  musicOwner: string,
  titleClass?: string,
  titleLingo?: string
) {
  const { actor } = useActor();
  // Only use secure endpoint when signed in and personalization not disabled
  const secure = Boolean(actor && actor.limitUsage === false);
  return useQuery<OwnerContextResponseMessage | null, Error>(
    ['ownerContext', secure, musicOwner, titleClass, titleLingo],
    async () => {
      try {
        return await fetchOwnerContext(musicOwner, titleClass, titleLingo, secure);
      } catch (err: any) {
        // Treat 404 as not-found, return null to avoid error boundary
        if (err.response?.status === 404) {
          return null;
        }
        throw err;
      }
    },
    { enabled: !!musicOwner, suspense: false, retry: false }
  );
}

// Hook: Title Suggest
export function useTitleSuggestQuery(query: string) {
  const { actor } = useActor();
  // Only use secure endpoint when signed in and personalization not disabled
  const secure = Boolean(actor && actor.limitUsage === false);
  return useQuery<TitleSuggestResponseMessage, Error>(
    ['titleSuggest', secure, query],
    () => fetchTitleSuggest(query, secure),
    {
      enabled: query.trim().length > 0,
      // Do not suspend the entire app on suggest loading
      suspense: false,
      // Handle errors locally rather than via global error boundary
      useErrorBoundary: false,
      // Keep previous suggestions until new ones are fetched to avoid flicker
      keepPreviousData: true,
    }
  );
}

/**
 * Hook: Class Context
 * Returns exploration catalog of videos for a given lingo and class.
 */
export function useClassContextQuery(
  titleLingo: string,
  titleClass: string,
  extraClass: string
) {
  const { actor } = useActor();
  const secure = Boolean(actor && actor.limitUsage === false);
  return useQuery<ClassContextResponseMessage | null, Error>(
    ['titleExplore', secure, titleLingo, titleClass, extraClass],
    async () => {
      try {
        return await fetchClassContext(titleLingo, titleClass, extraClass, secure);
      } catch (err: any) {
        if (err.response?.status === 404) {
          return null;
        }
        throw err;
      }
    },
    {
      enabled: !!titleClass,
      suspense: false,
      retry: false,
      useErrorBoundary: false,
    }
  );
}

/**
* Hook: Title Welcome
* Returns a list of music video recommendations for the user
*/
export function useTitleWelcomeQuery(titleLingo: string) {
  const { actor } = useActor();
  // Only use secure endpoint when signed in and personalization not disabled
  const secure = Boolean(actor && actor.limitUsage === false);
  return useQuery<TitleWelcomeResponseMessage, Error>(
    ['titleWelcome', secure, titleLingo],
    () => fetchTitleWelcome(titleLingo, secure),
    { enabled: !!titleLingo, suspense: false }
  );
}

/**
 * Hook: Actor Library
 * Returns user library, optionally filtered by class and language.
 */
export function useActorLibraryQuery(
    titleClass?: string,
    titleLingo?: string
) {
    return useQuery<ActorLibraryResponseMessage, Error>(
        ['titleLibrary', titleClass, titleLingo],
        () => fetchActorLibrary(
            titleClass || titleLingo ? { titleClass, titleLingo } : undefined
        ),
        { suspense: false }
    );
}

// Hook: Video Emotion Mutation
export function useActorEmotionMutation() {
    return useMutation<void, Error, { musicVideo: string; heartVideo: boolean }>(
        ({ musicVideo, heartVideo }) => writeActorEmotion(musicVideo, heartVideo)
    );
}
